import { useEffect, useContext } from "react";
import {
  FormControl,
  Autocomplete,
  TextField,
  SxProps,
  ListItemText,
} from "@mui/material";
import { useListAllFamilies } from "services/admin";
import { useListAdvisorFamilies } from "services/advisor";
import { Family } from "../../protogen/advisors_service_pb";
import { Member } from "protogen/common_pb";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "common/userUtils";

interface Props {
  selectedFamily: string | null;
  onChange: (familyRef: string | null, family?: Family | null) => void;
  error?: string | null;
  disabled?: boolean;
  title?: string;
  isAdmin?: boolean;
  sx?: SxProps;
}

export default ({
  selectedFamily,
  onChange,
  error,
  disabled,
  title = "Assignee",
  sx,
}: Props) => {
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = currentUser && isUserAdmin(currentUser);

  const { data: allFamiliesData, request: requestAllFamilies } =
    useListAllFamilies();
  const { data: advisorFamiliesData, request: requestAdvisorFamilies } =
    useListAdvisorFamilies();

  useEffect(() => {
    if (isAdmin) {
      requestAllFamilies();
    } else {
      requestAdvisorFamilies();
    }
  }, [isAdmin]);

  const familiesData = isAdmin ? allFamiliesData : advisorFamiliesData;
  const options = (familiesData?.families || []).sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const selectedFamilyObj =
    options.find((family: Family) => family.ref === selectedFamily) || null;

  return (
    <FormControl sx={{ width: "100%", ...sx }} fullWidth>
      <Autocomplete
        disabled={disabled}
        value={selectedFamilyObj}
        onChange={(_, value) => {
          onChange(value ? value.ref : null, value || null);
        }}
        options={options}
        getOptionLabel={(option) => option.name || ""}
        renderOption={(props, option) => (
          <li {...props} key={option.ref}>
            <ListItemText
              sx={{ overflow: "hidden" }}
              primary={option.name}
              secondary={option.familyMembers
                .map((m: Member) => m.displayName)
                .join(", ")}
            />
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={{
              ".MuiInputBase-root": {
                paddingTop: "3px",
                paddingBottom: "3px",
                cursor: "pointer",
              },
              "& .MuiInputBase-input": {
                cursor: "pointer",
                paddingLeft: "11px",
              },
            }}
            label={title}
            error={!!error}
          />
        )}
      />
    </FormControl>
  );
};
